import { createSelector } from "@reduxjs/toolkit";
import { mapKeys, merge, reduce, values } from "lodash-es";

import { buildSingleItem } from "shared/utils";
import { IJSONAPIItem, IDataKeys } from "shared/types";
import { API_ENDPOINTS } from "shared/constants";

import { dataSelector, buildData, dataMetaSelectorHelper } from "./helpers";
import { BandSettingsDataKeys, IBand } from "Bands/types";

export const bandMetaSelector = (id: string) => dataMetaSelectorHelper(API_ENDPOINTS.BAND(id));

const mapIdsToBands = (bandIds: IJSONAPIItem[], bands: IBand[]) => {
  const bandMap = mapKeys(bands, ({ id }) => id);
  return reduce(
    bandIds,
    (userBands: IBand[], { id, ...bandProps }) => {
      const band = merge({}, { id }, bandProps, bandMap?.[id]);

      if (band) {
        return [...userBands, band];
      }
      return userBands;
    },
    []
  );
};

const bandsSelector = createSelector(
  dataSelector,
  data => buildData(data as any, IDataKeys.band) as IBand[]
);

const userBandIdSelector = createSelector(
  dataSelector,
  data => buildData(data as any, IDataKeys.userBands) as IJSONAPIItem[]
);

export const userBandselector = createSelector(
  [userBandIdSelector, bandsSelector],
  (userBandIds, bands) => mapIdsToBands(userBandIds, bands)
);

const adminBandIdSelector = createSelector(
  dataSelector,
  data => buildData(data as any, IDataKeys.adminBands) as IJSONAPIItem[]
);

export const adminBandselector = createSelector(
  [adminBandIdSelector, bandsSelector],
  (adminBandIds, bands) => mapIdsToBands(adminBandIds, bands)
);

const dataBandSelector = createSelector(dataSelector, data => data.band);
const dataUserBandSelector = createSelector(dataSelector, data => data.userBands);
export const bandSelectorCreator = (id: string) =>
  createSelector(dataBandSelector, dataUserBandSelector, (bands, userBands) => {
    const userBand = userBands?.[API_ENDPOINTS.BAND(id)];
    const band = bands?.[API_ENDPOINTS.BAND(id)];

    if (band || userBand) {
      return buildSingleItem(merge({}, userBand, band)) as IBand;
    }
    return null;
  });

export function bandSettingsSelector<T>(key: BandSettingsDataKeys) {
  return createSelector(dataSelector, data => values(data[key]) as unknown as T[]);
}
